import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, ListOrdered, FileCog, FileCheck2, ShieldCheck } from "lucide-react";
import { scrollToId } from "@/lib/analytics";

const HEADLINE_LINES = [
  { text: "Give Your Team", accent: false },
  { text: "More Time for the", accent: false },
  { text: "Work That Matters", accent: true },
];

const TRUST_CHIPS = ["No monthly software fees", "Zero PHI", "Licensed per team — owned forever"];

function WorkflowVisual() {
  return (
    <div className="relative" data-testid="hero-workflow-visual">
      <div className="absolute -inset-3 rounded-3xl bg-navy/[0.04]" aria-hidden="true" />
      <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl shadow-navy/10 sm:p-7">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          De-identified bullets → VeraChart prompt → structured note
        </p>

        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-white"><ListOrdered className="h-4 w-4" aria-hidden="true" /></span>
            <div>
              <p className="text-sm font-semibold text-navy">1 · De-identified bullet points</p>
              <p className="text-xs text-slate-500">Placeholders only — never PHI</p>
            </div>
          </div>
          <ul className="space-y-1.5 font-mono text-xs text-slate-600">
            <li>• Client — community visit, 45 min</li>
            <li>• Reviewed PCP goals; practiced coping skills</li>
            <li>• Next step: employment appointment</li>
          </ul>
        </div>

        <div className="flex justify-center py-2.5" aria-hidden="true">
          <ArrowDown className="h-5 w-5 text-teal-600" />
        </div>

        <motion.div
          className="rounded-xl border border-teal-200 bg-teal-50 p-4"
          animate={{ boxShadow: ["0 0 0 0 rgba(13,148,136,0.18)", "0 0 0 8px rgba(13,148,136,0)", "0 0 0 0 rgba(13,148,136,0)"] }}
          transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
        >
          <div className="mb-3 flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-white"><FileCog className="h-4 w-4" aria-hidden="true" /></span>
            <div>
              <p className="text-sm font-semibold text-navy">2 · VeraChart prompt applied</p>
              <p className="text-xs text-slate-500">Inside your agency's approved AI platform</p>
            </div>
            <span className="ml-auto flex gap-1">
              {["PIE", "SOAP", "DAP"].map((f) => (
                <span key={f} className="rounded-full border border-teal-300 bg-white px-2 py-0.5 font-mono text-[10px] font-semibold text-teal-700">{f}</span>
              ))}
            </span>
          </div>
          <div className="space-y-2">
            {["64%", "84%", "52%"].map((w, i) => (
              <div key={i} className="h-2 rounded-full bg-teal-200/70" style={{ width: w }} />
            ))}
          </div>
        </motion.div>

        <div className="flex justify-center py-2.5" aria-hidden="true">
          <ArrowDown className="h-5 w-5 text-teal-600" />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-white"><FileCheck2 className="h-4 w-4" aria-hidden="true" /></span>
            <div>
              <p className="text-sm font-semibold text-navy">3 · Structured clinical note</p>
              <p className="text-xs text-slate-500">Reviewed, then copied into your official EHR</p>
            </div>
          </div>
          <div className="space-y-2.5">
            {["P", "I", "E"].map((k) => (
              <div key={k} className="flex items-center gap-2.5">
                <span className="w-5 text-center font-mono text-xs font-bold text-teal-700">{k}</span>
                <div className="h-2 flex-1 rounded-full bg-slate-200" />
              </div>
            ))}
          </div>
          <p className="mt-3.5 flex items-center gap-1.5 text-xs font-medium text-teal-700">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Reviewed &amp; approved by the professional
          </p>
        </div>
      </div>

      <motion.div
        className="absolute -right-3 -top-5 rounded-full border border-teal-200 bg-white px-4 py-2 shadow-lg sm:-right-6"
        animate={{ y: [0, -8, 0] }}
        transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
      >
        <span className="font-mono text-xs font-bold text-teal-700">Own it forever — no subscription</span>
      </motion.div>
    </div>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-white pt-[68px]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-teal-100/50 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-52 top-64 h-[380px] w-[380px] rounded-full bg-slate-100 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-14 sm:px-6 sm:pt-20 lg:px-8 lg:pb-24 lg:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal-700"
            >
              Behavioral Health Documentation Workflow for Community-Based Teams
            </motion.p>

            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              {HEADLINE_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.accent ? "text-teal-700" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              VeraChart gives community-based and facility-based teams an AI-assisted documentation workflow that
              turns field information into structured progress notes, CCAs, and PCPs in minutes — using your agency's
              existing AI tools.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <button
                type="button"
                data-testid="hero-view-packages-button"
                onClick={() => scrollToId("pricing")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-teal-600 px-8 py-3.5 font-display text-base font-semibold text-white shadow-lg shadow-teal-600/20 transition-all hover:-translate-y-0.5 hover:bg-teal-700 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
              >
                View Role Packages <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                data-testid="hero-see-how-button"
                onClick={() => scrollToId("how-it-works")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-8 py-3.5 font-display text-base font-semibold text-navy transition-colors hover:border-navy hover:bg-slate-50"
              >
                See How It Works
              </button>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2"
            >
              {TRUST_CHIPS.map((chip) => (
                <li key={chip} className="flex items-center gap-2 text-sm font-medium text-slate-600">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" />
                  {chip}
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div style={{ y: visualY }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}>
            <WorkflowVisual />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
